import React from 'react';
import { useAuth } from '../../../contexts/AuthContextLazy';
import { useClientFeatures } from '../../../hooks/useClientFeatures';
import { ClientFeatureGate } from '../../../components/portal/ClientFeatureGate';
import { MiniUpgradePrompt } from '../../../components/portal/UpgradePrompt';
import {
    ChartBarIcon,
    DocumentTextIcon,
    ChatBubbleLeftRightIcon,
    UserCircleIcon,
    FolderIcon,
    CalendarIcon,
    ArrowTrendingUpIcon,
    ShieldCheckIcon,
    BanknotesIcon,
    HomeModernIcon
} from '@heroicons/react/24/outline';

export const ClientDashboard: React.FC = () => {
    const { userProfile, organization } = useAuth();
    const { hasFeature, getTierInfo } = useClientFeatures();
    const tier = getTierInfo();

    // Quick stats based on tier
    const stats = [
        {
            name: 'Net Worth',
            value: '$2.3M',
            change: '+4.75%',
            feature: 'portfolio_view',
            icon: BanknotesIcon,
            color: 'green'
        },
        {
            name: 'Portfolio Value',
            value: '$1.8M',
            change: '+12.3%',
            feature: 'portfolio_view',
            icon: ArrowTrendingUpIcon,
            color: 'blue'
        },
        {
            name: 'Documents',
            value: '47',
            change: '3 new',
            feature: 'basic_documents',
            icon: DocumentTextIcon,
            color: 'purple'
        },
        {
            name: 'Messages',
            value: '12',
            change: '2 unread',
            feature: 'basic_messaging',
            icon: ChatBubbleLeftRightIcon,
            color: 'amber'
        }
    ];

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

            {/* Stats Grid */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
                {stats.map((stat) => {
                    const Icon = stat.icon;
                    const hasAccess = hasFeature(stat.feature as any);

                    return (
                        <div
                            key={stat.name}
                            className={`relative bg-white overflow-hidden shadow rounded-lg ${!hasAccess ? 'opacity-75' : ''
                                }`}
                        >
                            <div className="p-5">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <Icon className={`h-6 w-6 text-${stat.color}-600`} />
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 truncate">
                                                {stat.name}
                                            </dt>
                                            <dd className="flex items-baseline">
                                                {hasAccess ? (
                                                    <>
                                                        <div className="text-2xl font-semibold text-gray-900">
                                                            {stat.value}
                                                        </div>
                                                        <div className={`ml-2 flex items-baseline text-sm font-semibold text-${stat.color}-600`}>
                                                            {stat.change}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="text-lg text-gray-400">
                                                        <MiniUpgradePrompt inline />
                                                    </div>
                                                )}
                                            </dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                            {!hasAccess && (
                                <div className="absolute inset-0 bg-gray-50 bg-opacity-50" />
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column - Portfolio & Goals */}
                <div className="lg:col-span-2 space-y-8">
                    {/* Portfolio Snapshot */}
                    <ClientFeatureGate
                        feature="portfolio_view"
                        featureName="Portfolio Overview"
                        requiredTier="pro"
                    >
                        <div className="bg-white portal-content-shadow rounded-lg p-6">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                <ChartBarIcon className="h-5 w-5 mr-2 text-blue-600" />
                                Portfolio Overview
                            </h2>
                            <div className="space-y-4">
                                <div className="border rounded-lg p-4">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-sm font-medium text-gray-700">Asset Allocation</span>
                                        <span className="text-sm text-gray-500">Well Diversified</span>
                                    </div>
                                    <div className="grid grid-cols-4 gap-2 text-xs">
                                        <div>
                                            <div className="h-2 bg-blue-500 rounded"></div>
                                            <p className="mt-1">Stocks 45%</p>
                                        </div>
                                        <div>
                                            <div className="h-2 bg-green-500 rounded"></div>
                                            <p className="mt-1">Bonds 30%</p>
                                        </div>
                                        <div>
                                            <div className="h-2 bg-purple-500 rounded"></div>
                                            <p className="mt-1">Real Estate 15%</p>
                                        </div>
                                        <div>
                                            <div className="h-2 bg-amber-500 rounded"></div>
                                            <p className="mt-1">Cash 10%</p>
                                        </div>
                                    </div>
                                </div>

                                <ClientFeatureGate
                                    feature="realtime_portfolio"
                                    featureName="Real-time Portfolio Data"
                                    requiredTier="premium"
                                >
                                    <div className="border border-purple-200 bg-purple-50 rounded-lg p-4">
                                        <p className="text-sm font-medium text-purple-900">Live Market Updates</p>
                                        <p className="text-xs text-purple-700 mt-1">
                                            Your portfolio is up $12,450 today (+0.69%)
                                        </p>
                                    </div>
                                </ClientFeatureGate>
                            </div>
                        </div>
                    </ClientFeatureGate>

                    {/* Goals Tracking */}
                    <ClientFeatureGate
                        feature="goal_management"
                        featureName="Goal Tracking"
                        requiredTier="pro"
                    >
                        <div className="bg-white portal-content-shadow rounded-lg p-6">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                <FolderIcon className="h-5 w-5 mr-2 text-green-600" />
                                Financial Goals
                            </h2>
                            <div className="space-y-4">
                                <div className="border rounded-lg p-4">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="font-medium">Retirement Savings</span>
                                        <span className="text-sm text-green-600 font-medium">78%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div className="bg-green-600 h-2 rounded-full" style={{ width: '78%' }}></div>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">$1.56M of $2M goal</p>
                                </div>

                                <div className="border rounded-lg p-4">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="font-medium">Children's Education</span>
                                        <span className="text-sm text-blue-600 font-medium">45%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div className="bg-blue-600 h-2 rounded-full" style={{ width: '45%' }}></div>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2">$225K of $500K goal</p>
                                </div>
                            </div>
                        </div>
                    </ClientFeatureGate>

                    {/* Tax Center */}
                    <ClientFeatureGate
                        feature="tax_center"
                        featureName="Tax Optimization Center"
                        requiredTier="premium"
                    >
                        <div className="bg-white portal-content-shadow rounded-lg p-6">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                <ShieldCheckIcon className="h-5 w-5 mr-2 text-purple-600" />
                                Tax Optimization
                            </h2>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="border rounded-lg p-4">
                                    <p className="text-sm font-medium text-gray-700">2024 Tax Savings</p>
                                    <p className="text-2xl font-bold text-green-600">$34,250</p>
                                    <p className="text-xs text-gray-500 mt-1">Through optimization strategies</p>
                                </div>
                                <div className="border rounded-lg p-4">
                                    <p className="text-sm font-medium text-gray-700">Available Credits</p>
                                    <p className="text-2xl font-bold text-blue-600">$12,500</p>
                                    <p className="text-xs text-gray-500 mt-1">Unclaimed deductions</p>
                                </div>
                            </div>
                        </div>
                    </ClientFeatureGate>
                </div>

                {/* Right Column - Quick Actions & Messages */}
                <div className="space-y-8">
                    {/* Quick Actions */}
                    <div className="bg-white portal-content-shadow rounded-lg p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
                        <div className="space-y-3">
                            <button className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg flex items-center">
                                <DocumentTextIcon className="h-5 w-5 mr-3 text-gray-600" />
                                <span className="text-sm font-medium">Upload Document</span>
                            </button>

                            <ClientFeatureGate
                                feature="meeting_scheduler"
                                featureName="Meeting Scheduler"
                                requiredTier="pro"
                            >
                                <button className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg flex items-center">
                                    <CalendarIcon className="h-5 w-5 mr-3 text-gray-600" />
                                    <span className="text-sm font-medium">Schedule Meeting</span>
                                </button>
                            </ClientFeatureGate>

                            <ClientFeatureGate
                                feature="client_requests_pro"
                                featureName="Service Requests"
                                requiredTier="pro"
                            >
                                <button className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg flex items-center">
                                    <UserCircleIcon className="h-5 w-5 mr-3 text-gray-600" />
                                    <span className="text-sm font-medium">Submit Request</span>
                                </button>
                            </ClientFeatureGate>
                        </div>
                    </div>

                    {/* Recent Messages */}
                    <div className="bg-white portal-content-shadow rounded-lg p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Messages</h2>
                        <div className="space-y-3">
                            <div className="border-l-4 border-amber-400 bg-amber-50 p-3 rounded">
                                <p className="text-sm font-medium text-gray-900">Tax Planning Update</p>
                                <p className="text-xs text-gray-600 mt-1">
                                    New tax savings opportunities identified for Q4
                                </p>
                                <p className="text-xs text-gray-500 mt-2">2 hours ago</p>
                            </div>

                            <div className="border-l-4 border-blue-400 bg-blue-50 p-3 rounded">
                                <p className="text-sm font-medium text-gray-900">Portfolio Review</p>
                                <p className="text-xs text-gray-600 mt-1">
                                    Quarterly rebalancing recommendations ready
                                </p>
                                <p className="text-xs text-gray-500 mt-2">Yesterday</p>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};
